export interface ProgressOverlayRenderData {
  seasonLine: string
  dailyTitle: string
  missions: string[]
  bpTitle: string
  bpTrack: string
  bpState: string
  claimLabel: string
  claimEnabled: boolean
}

export interface ProgressOverlayOptions {
  title: string
  loadingText: string
  offlineText: string
  leaderboardLabel: string
  backLabel: string
  onLeaderboard: () => void
  onBack: () => void
  onClaim: () => void
}

export interface ProgressOverlayHandle {
  setLoading: () => void
  setOffline: () => void
  setStatus: (text: string, tone?: 'good' | 'bad' | 'sub') => void
  render: (data: ProgressOverlayRenderData) => void
  destroy: () => void
}

export function mountProgressOverlay(options: ProgressOverlayOptions): ProgressOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-progress-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-progress-panel'

  const title = document.createElement('h1')
  title.className = 'game-progress-title'
  title.textContent = options.title

  const status = document.createElement('p')
  status.className = 'game-progress-status'

  const content = document.createElement('div')
  content.className = 'game-progress-content'

  const footer = document.createElement('div')
  footer.className = 'game-progress-footer'
  footer.append(
    button(options.leaderboardLabel, 'game-progress-btn game-progress-leaderboard', options.onLeaderboard),
    button(options.backLabel, 'game-progress-btn', options.onBack),
  )

  panel.append(title, status, content, footer)
  root.append(panel)
  document.body.append(root)

  const setStatus = (text: string, tone: 'good' | 'bad' | 'sub' = 'sub'): void => {
    status.textContent = text
    status.hidden = !text
    status.dataset.tone = tone
  }

  const render = (data: ProgressOverlayRenderData): void => {
    setStatus('')
    content.innerHTML = ''
    const season = document.createElement('section')
    season.className = 'game-progress-card game-progress-season'
    season.innerHTML = `<h2>${escapeHtml(data.seasonLine)}</h2>`

    const daily = document.createElement('section')
    daily.className = 'game-progress-card'
    daily.innerHTML = `<h2>${escapeHtml(data.dailyTitle)}</h2>`
    const missionList = document.createElement('ul')
    missionList.className = 'game-progress-list'
    for (const line of data.missions) {
      const li = document.createElement('li')
      li.textContent = line
      missionList.append(li)
    }
    daily.append(missionList)

    const bp = document.createElement('section')
    bp.className = 'game-progress-card'
    bp.innerHTML = `<h2>${escapeHtml(data.bpTitle)}</h2>`
    const track = document.createElement('p')
    track.className = 'game-progress-muted'
    track.textContent = data.bpTrack
    const state = document.createElement('p')
    state.className = data.claimEnabled ? 'game-progress-claimable' : 'game-progress-muted'
    state.textContent = data.bpState
    const claim = button(data.claimLabel, 'game-progress-btn game-progress-claim', options.onClaim)
    claim.disabled = !data.claimEnabled
    bp.append(track, state, claim)

    content.append(season, daily, bp)
  }

  setStatus(options.loadingText)

  return {
    setLoading: () => {
      content.innerHTML = ''
      setStatus(options.loadingText)
    },
    setOffline: () => {
      content.innerHTML = ''
      setStatus(options.offlineText, 'bad')
    },
    setStatus,
    render,
    destroy: () => root.remove(),
  }
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = className
  btn.textContent = label
  btn.addEventListener('click', onClick)
  return btn
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
